import { useMemo } from 'react';
import { latestConversation } from '@/components/agent-chat';
import type { SurfaceDescriptor } from '@/components/surface-host/surface-host';
import type { AgentConversationSummary } from '@/services/agents/config';
import { useGetArtifact } from '@/hooks/queries/artifacts/use-get-artifact';
import { useAgentConversations } from './use-agent-conversations';

export interface SurfaceConversationResolution {
  /** The conversation to resume, or `null` for a fresh thread. */
  conversationId: string | null;
  /**
   * True while the underlying conversations (and, for an artifact surface,
   * the artifact detail) are still loading -- `conversationId` is `null` in
   * this window too, but it does not yet mean "fresh thread": callers must
   * hold off treating it as resolved until this clears, or a message sent in
   * that window starts an unrelated conversation instead of resuming the
   * linked one.
   */
  isResolving: boolean;
}

function mostRecentMatching(
  conversations: AgentConversationSummary[],
  matches: (conversation: AgentConversationSummary) => boolean,
): AgentConversationSummary | null {
  return latestConversation(conversations.filter(matches));
}

/**
 * Resolve which conversation the chat rail should resume for the given surface.
 *
 * Resolution rules (first match wins):
 * - **artifact**: the artifact's own `conversation_id`; else the most recent
 *   conversation that lists this artifact in `artifact_ids`; else the most
 *   recent conversation that lists its source workflow in `workflow_ids`
 * - **workflow**: the most recent conversation that lists that workflow in
 *   `workflow_ids` (including threads started while viewing an artifact of
 *   that workflow, which record the source workflow alongside the artifact)
 * - **artifact_gallery** or `null`: `null` — open a fresh thread
 *
 * The "fallback to whatever thread you were in last" behaviour is intentionally
 * absent: a surface that has never been chatted about should start a fresh
 * thread rather than silently resuming an unrelated one.
 */
export function useSurfaceConversation(
  surface: SurfaceDescriptor | null,
  agentId: string | undefined,
): SurfaceConversationResolution {
  const { data: conversations = [], isLoading: isLoadingConversations } =
    useAgentConversations(agentId);

  // Only fetch the artifact when we are actually on an artifact surface, to
  // avoid a spurious network request for every other surface kind.
  const artifactId = surface?.kind === 'artifact' ? surface.artifactId : null;
  const { data: artifact, isLoading: isLoadingArtifact } =
    useGetArtifact(artifactId);

  const isResolving =
    !!surface &&
    (isLoadingConversations || (!!artifactId && isLoadingArtifact));

  const conversationId = useMemo(() => {
    if (!surface) return null;

    if (surface.kind === 'artifact') {
      // 1. The artifact's own conversation_id (written at promotion time).
      const ownConversationId = artifact?.conversation_id;
      if (ownConversationId) {
        const stillThere = conversations.some(
          (c) => c.conversation_id === ownConversationId,
        );
        if (stillThere) return ownConversationId;
      }

      // 2. Most recent conversation that explicitly lists this artifact.
      const byArtifact = mostRecentMatching(conversations, (c) =>
        Boolean(c.artifact_ids?.includes(surface.artifactId)),
      );
      if (byArtifact) return byArtifact.conversation_id;

      // 3. Most recent conversation that lists the artifact's source workflow
      // (a canvas thread about the workflow that produced this artifact).
      const sourceWorkflowId = artifact?.source_workflow_id;
      if (sourceWorkflowId) {
        const byWorkflow = mostRecentMatching(conversations, (c) =>
          Boolean(c.workflow_ids?.includes(sourceWorkflowId)),
        );
        if (byWorkflow) return byWorkflow.conversation_id;
      }

      return null;
    }

    if (surface.kind === 'workflow') {
      const byWorkflow = mostRecentMatching(conversations, (c) =>
        Boolean(c.workflow_ids?.includes(surface.workflowId)),
      );
      return byWorkflow?.conversation_id ?? null;
    }

    // artifact_gallery and any future surface kinds: fresh thread.
    return null;
  }, [surface, conversations, artifact]);

  return { conversationId, isResolving };
}

/**
 * The most-recently-active conversation for this agent, regardless of surface.
 *
 * Used as a fallback by pages that don't yet have surface-level linkage wired
 * up (e.g. the top-level agent chat page).
 */
export function useLatestConversation(
  agentId: string | undefined,
): string | null {
  const { data: conversations = [] } = useAgentConversations(agentId);
  return latestConversation(conversations)?.conversation_id ?? null;
}
